import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import {
  GROUP_BY_APP_VALUE,
  SCREEN_SIZE_MOBILE_XLARGE,
  SCREEN_WIDTH_QUERIES,
} from '../../../utils/constant';
import Tile from '../Tile';

import {
  getDynamicStyles,
  getNodeDataToViewForTiles,
  getTotalChildrenCount,
  removeChildren,
} from './utils';

import './Cards.css';

function Cards({
  data,
  level,
  handleSidePanel,
  handleAutoSelectParent,
  setMergedLists,
  actionPopoverOpenId,
  setActionPopoverOpenId,
  isScrolling,
}) {
  const [colsToShow, setColsToShow] = useState({});
  const [disableTransition, setDisableTransition] = useState(false);
  const [treeStyles, setTreeStyles] = useState({});
  const [shouldGetStyles, setShouldGetStyles] = useState(false);
  const allCardsRef = useRef(null);

  const {
    core: { isLoading, campaignType },
    global: { mediaQueryValue },
    sidePanel: { isSidePanelVisible },
    viewControls: { groupByOptions, colsFilters },
  } = useSelector((state) => state?.certifications?.details);

  const isLevelZero = level === 0;
  const isLevelOne = level === 1;
  const selectedGroupByValue = groupByOptions?.find(
    (option) => option.isChecked
  )?.value;
  const isUsingGroupBy = selectedGroupByValue === GROUP_BY_APP_VALUE;

  const mobileXLargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );
  const isMobileXlargeWidth = mobileXLargeQuery.value <= mediaQueryValue;

  const cardsClasses = classnames(
    'Cards',
    `Cards--${level}`,
    isUsingGroupBy && 'Cards--isUsingGroupBy',
    isSidePanelVisible && 'Cards--isSidePanelVisible'
  );

  // Remove transitions from Card trees for durations of transition after loading
  useEffect(() => {
    let timer;

    if (isLoading) {
      setDisableTransition(true);
    } else {
      timer = setTimeout(() => {
        setDisableTransition(false);
      }, 800);
    }

    return () => clearTimeout(timer);
  }, [isLoading]);

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.target.classList.contains('Cards-tree--isBeingRemoved')
        ) {
          const parent = mutation.target.closest('.Cards-tree--0');
          const subParent = mutation.target.closest('.Cards-tree--1');

          if (
            parent &&
            !parent.classList.contains('Cards-tree--isBeingRemoved')
          )
            parent.style.height = 'auto';

          if (
            subParent &&
            !subParent.classList.contains('Cards-tree--isBeingRemoved')
          )
            parent.style.height = 'auto';
        }
      });
    });

    const config = {
      attributes: true,
      subtree: true,
      attributeFilter: ['class'],
    };

    if (allCardsRef.current) {
      observer.observe(allCardsRef.current, config);
      setShouldGetStyles(true);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (colsFilters) setColsToShow(colsFilters);
  }, [colsFilters]);

  const getTreeClasses = (node, treeIndex) => {
    const isTreeIndexZero = treeIndex === 0;
    const isTreeIndexEnd = treeIndex === data.length - 1;
    const isEnt = node.CERT_ENTITLEMENT_VALUEKEY <= 0;
    const hasNodeChildren = node?.children?.length > 0;

    const isTopTile = isTreeIndexZero && !isLevelZero;
    const isBottomTile = isTreeIndexEnd && !isLevelZero;
    const hasEntChild = isLevelOne && isEnt && hasNodeChildren;

    const defaultTreeClasses = classnames(
      'Cards-tree',
      `Cards-tree--${level}`,
      `Cards-treeIndex--${treeIndex}`,
      isTopTile && 'Cards-tree--isTopTile',
      isBottomTile && 'Cards-tree--isBottomTile',
      hasEntChild && 'Cards-tree--hasEntChild',
      !hasNodeChildren && 'Cards-tree--hasNoChildren'
    );

    return defaultTreeClasses;
  };

  const getTreeStyles = (node) => {
    const { count: totalChildrenCount, levelOneChildrenWithChildren } =
      getTotalChildrenCount(node, level);

    const dynamicTreeStyles = getDynamicStyles(
      totalChildrenCount,
      levelOneChildrenWithChildren,
      level,
      isMobileXlargeWidth,
      isLevelZero && isUsingGroupBy
    );

    return dynamicTreeStyles;
  };

  useEffect(() => {
    if (!data) return;

    const updateTreeStyles = () => {
      const newTreeStyles = data.reduce((acc, node, index) => {
        const dynamicTreeStyles = getTreeStyles(node);

        acc[index] = dynamicTreeStyles;

        return acc;
      }, {});

      setTreeStyles(newTreeStyles);
    };

    updateTreeStyles();
  }, [data, shouldGetStyles, isMobileXlargeWidth]);

  if (!data) return null;

  return (
    <div className={cardsClasses} ref={allCardsRef}>
      {data?.map((node, index) => {
        const nodeData = removeChildren(node);
        const filteredNodeData = getNodeDataToViewForTiles(
          nodeData,
          colsToShow,
          campaignType
        );
        const treeClassNames = getTreeClasses(node, index);

        return (
          <div
            id={node?.id}
            key={node?.tracker?.id}
            style={{
              ...treeStyles[index],
              transition: disableTransition ? 'none' : '',
            }}
            className={treeClassNames}>
            <Tile
              data={filteredNodeData}
              nodeData={nodeData}
              level={level}
              handleSidePanel={() => handleSidePanel(nodeData)}
              handleAutoSelectParent={handleAutoSelectParent}
              setMergedLists={setMergedLists}
              actionPopoverOpenId={actionPopoverOpenId}
              setActionPopoverOpenId={setActionPopoverOpenId}
              isScrolling={isScrolling}
            />
            {node?.children?.length !== 0 && (
              <Cards
                data={node?.children || []}
                level={level + 1}
                handleSidePanel={handleSidePanel}
                handleAutoSelectParent={handleAutoSelectParent}
                setMergedLists={setMergedLists}
                actionPopoverOpenId={actionPopoverOpenId}
                setActionPopoverOpenId={setActionPopoverOpenId}
                isScrolling={isScrolling}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

Cards.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  level: PropTypes.number,
  handleSidePanel: PropTypes.func.isRequired,
  handleAutoSelectParent: PropTypes.func.isRequired,
  setMergedLists: PropTypes.func.isRequired,
  actionPopoverOpenId: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  setActionPopoverOpenId: PropTypes.func.isRequired,
  isScrolling: PropTypes.bool.isRequired,
};

Cards.defaultProps = {
  level: 0,
  actionPopoverOpenId: false,
};

export default Cards;
